import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { computeDownloadLayout } from "@/lib/download-layout";
import {
  hrefForBuild,
  iconForBuild,
  labelForBuild,
} from "@/lib/download-build-present";
import type { BuildId, DownloadLinks } from "@/lib/platform";
import {
  useMacCpuArch,
  useOS,
  useWindowsCpuArch,
} from "@/hooks/use-client-os";
import { CurrentSystemBadge } from "@/components/CurrentSystemBadge";

const emptyLinks: DownloadLinks = {
  windowsX64: "",
  windowsArm64: "",
  macIntel: "",
  macArm: "",
};

export function DownloadSection() {
  const os = useOS();
  const windowsCpuArch = useWindowsCpuArch(os);
  const macCpuArch = useMacCpuArch(os);
  const [moreVersionsOpen, setMoreVersionsOpen] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState<DownloadLinks>(emptyLinks);

  useEffect(() => {
    fetch("/api/downloads")
      .then((res) => res.json())
      .then((data: DownloadLinks) => setDownloadLinks(data))
      .catch((err) => console.error("Failed to fetch download links", err));
  }, []);

  const { matchBuild, primaryFirst, primarySecond, moreBuilds } = useMemo(
    () => computeDownloadLayout(os, windowsCpuArch, macCpuArch),
    [os, windowsCpuArch, macCpuArch],
  );

  const primaryIds: [BuildId, BuildId] = [primaryFirst, primarySecond];

  return (
    <section className="w-full max-w-5xl mx-auto px-6 mb-20">
      <div className="flex flex-col items-center justify-center">
        <div className="flex flex-wrap justify-center gap-4">
          {primaryIds.map((id) => {
            const isMatch = matchBuild !== null && id === matchBuild;
            const href = hrefForBuild(id, downloadLinks);
            const label = labelForBuild(id);
            const Icon = iconForBuild(id);
            return (
              <div key={id} className="relative group">
                {isMatch && <CurrentSystemBadge />}
                <Button
                  size="lg"
                  variant="outline"
                  className={`h-14 px-6 md:px-8 text-base md:text-lg rounded-full transition-colors ${
                    isMatch
                      ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 hover:text-blue-800 dark:hover:text-blue-200"
                      : "hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  }`}
                  asChild
                >
                  <a href={href}>
                    <Icon
                      className={
                        isMatch
                          ? "mr-2 h-5 w-5 text-blue-600 dark:text-blue-300"
                          : id.startsWith("mac")
                            ? "mr-2 h-5 w-5 text-neutral-900 dark:text-neutral-100"
                            : "mr-2 h-5 w-5 text-blue-600"
                      }
                    />
                    {label}
                  </a>
                </Button>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col items-center">
          <button
            type="button"
            onClick={() => setMoreVersionsOpen((v) => !v)}
            className="text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors flex items-center gap-1.5 opacity-80 hover:opacity-100"
            aria-expanded={moreVersionsOpen}
          >
            更多版本
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform duration-200",
                moreVersionsOpen && "rotate-180",
              )}
            />
          </button>
          {moreVersionsOpen && (
            <div className="mt-3 flex flex-col sm:flex-row flex-wrap justify-center gap-x-8 gap-y-2">
              {moreBuilds.map((id) => {
                const href = hrefForBuild(id, downloadLinks);
                const label = labelForBuild(id);
                const Icon = iconForBuild(id);
                return (
                  <a
                    key={id}
                    href={href}
                    className="text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors flex items-center justify-center gap-1.5 opacity-80 hover:opacity-100"
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    {label}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-8 text-center max-w-lg mx-auto space-y-3">
          <p className="text-sm text-neutral-400 leading-relaxed">
            Proxdav 相当于一个轻量级的 Charles、Fiddler 或
            Proxyman，专注于快速代理切换、零配置 HTTPS 解密及可编程重写规则。
          </p>
        </div>
      </div>
    </section>
  );
}
